import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { autoSessionBridge } from '@/tauri/chronicle/auto-session';

/**
 * Syncs the session ID of the route currently open to the Rust auto-session
 * watcher. When the user is on a session detail page, Rust will skip
 * auto-session creation on a Chronicle device-initiated start — the frontend's
 * `useChronicleSessionControl` will start recording on the existing session
 * instead. Without this sync, Rust races with the frontend and creates an
 * orphan "Untitled" (duplicate) session whenever a recording is started from
 * the Remote while a session page is open.
 *
 * Matches BOTH the legacy `/scribe/session/[...id]` route and the new
 * session-list `/scribe/sessions/[[...id]]` route — `/scribe/session/{id}`
 * redirects to `/scribe/sessions/{id}` when `release-...new-session-list` is on.
 * This mirrors `getCurrentSessionId`'s `startsWith('/scribe/session')` check;
 * an exact-match here silently failed on the plural route, so Rust never saw a
 * route session and auto-created a duplicate alongside the frontend's.
 */
export function useChronicleSyncRouteSession() {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const isSessionPage = router.pathname?.startsWith('/scribe/session') ?? false;
    const routeId = router.query.id;
    const sessionId = Array.isArray(routeId) ? routeId.at(0) : routeId;

    void autoSessionBridge.setCurrentRouteSession(
      isSessionPage && sessionId ? sessionId : null
    );
  }, [router.isReady, router.pathname, router.query.id]);
}
